<template>
	<Page theme="gray" animate>
		<template v-slot:content>
			<div class="top">
				<SafeAreaTop :theme="closeTheme" :closePosition="closePosition" @close="onClose"> </SafeAreaTop>
				<img :src="require(`@/assets/post/workbooks/${language}.png`)" alt="" />
			</div>

			<div id="form" class="container">
				<div class="title" :class="productsTheme">{{ t('workbooks.title') }}</div>

				<div class="form-group" :class="productsTheme">
					<label for="" class="form-group--label">{{ t('workbooks.your_email') }}</label>
					<input
						type="email"
						v-model="data.email"
						@input="v$.$reset()"
						:class="{ error: v$.email.$error }"
						:disabled="hasEmail"
						class="form-control"
						:placeholder="t('workbooks.enter_your_email')"
					/>

					<div class="form-group--note">{{ t('workbooks.we_will_send') }}</div>
				</div>

				<Products :products="products" :active="activeProduct" @select="handleSelect" :theme="productsTheme" />

				<div class="workbooks">
					<div class="workbook">
						<div class="workbook-row">
							<div class="workbook--image right">
								<img :src="require(`@/assets/post/nutrition/${language}.png`)" alt="" />
							</div>
							<div class="workbook--title left">{{ t('workbooks.healthy_eating_workbook') }}</div>
						</div>
						<div class="workbook--description">
							{{ workbookDescriptionHealthyEating }}
						</div>
					</div>
					<div class="workbook">
						<div class="workbook-row">
							<div class="workbook--title right">{{ t('workbooks.workout_workbook') }}</div>
							<div class="workbook--image left">
								<img :src="require(`@/assets/post/workout/${language}.png`)" alt="" />
							</div>
						</div>
						<div class="workbook--description" :class="customClassWorkbookWorkoutDescription">
							{{ workbookDescriptionWorkout }}
						</div>
					</div>
				</div>
			</div>

			<modal :isOpen="showCongratulationsModal">
				<template v-slot:body>
					<CongratulationsModal :email="data.email" @close="onCloseCongratulationsModal" />
				</template>
			</modal>

			<Popup v-if="isShowingWebPaymentPopup" title="PaymentMethodPopup" :path="['AddProductPaywall']">
				<BillingForm @purchase="onPurchase" :product="activeProduct" mode="purchase" />
			</Popup>
		</template>

		<template v-slot:footer>
			<div class="container">
				<p-button
					v-scroll-to="{ el: '#form', offset: -20, onDone: onScrollDone }"
					label=""
					:loading="isLoading"
					cls="btn-primary btn-block"
				>
					{{ t('workbooks.continue') }}
				</p-button>
			</div>
		</template>
	</Page>
</template>

<script>
import SafeAreaTop from '@/components/SafeAreaTop.vue'
import Page from '@/components/Page'
import Products from '@/components/products/ios/Products'
import Modal from '@/components/Modal'
import PButton from '@/components/Button'
import CongratulationsModal from '@/components/CongratulationsModal'
import { BillingForm, useBilling } from '@/modules/billing'
import Popup from '@/components/Popup'

import { computed, reactive, ref } from '@vue/reactivity'
import { email, required } from '@vuelidate/validators'
import { inject, onMounted, watch } from '@vue/runtime-core'
import { useI18n } from 'vue-i18n'

import useVuelidate from '@vuelidate/core'
import useApi from '@/composition/api.js'
import useEnv from '@/composition/env.js'
import { useStore } from 'vuex'
// import { i18n } from './i18n'
import md5 from 'md5'
import { useRoute, useRouter } from 'vue-router'
import useConfig from '@/composition/config.js'

const NUTRITION_WORKBOOK = 'nutrition'
const WORKOUT_WORKBOOK = 'workout'
const BUNDLE_WORKBOOK = 'bundle'

export default {
	components: {
		Page,
		Products,
		Modal,
		CongratulationsModal,
		SafeAreaTop,
		PButton,
		BillingForm,
		Popup
	},

	setup() {
		const api = useApi()
		const store = useStore()
		const env = useEnv()
		const appboy = inject('appboy')
		const amplitude = inject('amplitude')
		const { t, locale } = useI18n()
		const config = useConfig()
		const router = useRouter()
		const route = useRoute()
		const billing = useBilling()

		const language = computed(() => locale.value)

		const lastPaymentMethod = computed(() => store.state.lastPaymentMethod)

		const productsTheme = computed(() => config.get({ key: 'workbooks.theme', default_value: 'white' }))
		const closeTheme = computed(() => config.get({ key: 'workbooks.close_buttom.theme', default_value: 'dark' }))
		const closePosition = computed(() => config.get({ key: 'workbooks.close_buttom.position', default_value: 'left' }))

		const workbookDescriptionHealthyEating = computed(() =>
			config.get({
				key: 'workbooks.description_healthy_eating',
				default_value: t('workbooks.healthy_eating_workbook_description')
			})
		)
		const workbookDescriptionWorkout = computed(() =>
			config.get({ key: 'workbooks.description_workout', default_value: t('workbooks.workout_workbook_description') })
		)

		const customClassWorkbookWorkoutDescription = computed(() =>
			config.get({ key: 'workbooks.workout_decription_custom_class', default_value: null })
		)

		const products = computed(() =>
			store.getters['products/getData'](
				Object.values({
					bunddle: Object.assign(
						{
							id: 'simple_all_workbooks',
							web_name: t('workbooks.workbook_bundle'),
							text: t('workbooks.workbook_bundle'),
							subtitle: '[price]',
							type: BUNDLE_WORKBOOK
						},
						config.get({ key: 'workbooks.bunddle', default_value: {} })
					),
					nutrition: Object.assign(
						{
							id: 'simple_nutrition_workbook',
							web_name: t('workbooks.healthy_eating_workbook'),
							text: t('workbooks.healthy_eating_workbook'),
							subtitle: '[price]',
							type: NUTRITION_WORKBOOK
						},
						config.get({ key: 'workbooks.nutrition', default_value: {} })
					),
					workout: Object.assign(
						{
							id: 'simple_workout_workbook',
							web_name: t('workbooks.workout_workbook'),
							text: t('workbooks.workout_workbook'),
							subtitle: '[price]',
							type: WORKOUT_WORKBOOK
						},
						config.get({ key: 'workbooks.workout', default_value: {} })
					)
				})
			)
		)

		let activeProduct = ref(products.value[0])

		const handleSelect = (product) => {
			activeProduct.value = product
		}

		/** Loading */
		let isLoading = computed(() => store.state.isLoading)

		/** Validation */
		const data = reactive({
			email: null
		})
		const rules = {
			email: { required, email }
		}
		const v$ = useVuelidate(rules, data)

		/** Email */
		let hasEmail = computed(() => Boolean(store.state.user.email))
		const checkEmail = () => {
			if (hasEmail.value) {
				data.email = store.state.user.email
			}
		}
		watch(() => hasEmail.value, checkEmail)
		onMounted(() => {
			checkEmail()
			amplitude.logEvent(store.getters['getAmplitudeEventName']('AddProductPaywallShow'), {
				product_ids: products.value.map((product) => product.id),
				product_type: 'workbook',
				prices: products.value.map((product) => product.product_price),
				currency: activeProduct.value.currency
			})
		})

		/** Congratulations Modal */
		let showCongratulationsModal = ref(false)

		let userId = computed(() => store.state.user.user_id)

		const close = (Status = 'Close', product = {}) => {
			amplitude.logEvent('Post Paywall - Close', {
				Layout: 'Workbooks',
				From: store.getters['getSource'],
				Step: 'Workbooks',
				Status
			})

			amplitude.logEvent(store.getters['getAmplitudeEventName']('AddProductPaywallDone'), {
				action: Status.toLowerCase(),
				product_type: 'workbook',
				...product
			})

			/**
			 * START: Emotional Course
			 */
			const canShowEmotionalCourse = computed(() =>
				config.get({ key: 'emotional_course_supported', default_value: false })
			)
			if (canShowEmotionalCourse.value) {
				return router.push({
					name: 'grab-a-snack'
				})
			}
			/**
			 * END: Emotional Course
			 */

			const before_close = computed(() => config.get({ key: 'beforeClose', default_value: 'after-purchase' }))

			if (before_close.value === 'after-purchase' && route.query.Status === 'Subscribe') {
				router.push({
					name: before_close.value
				})
			} else if (before_close.value === 'rejection-reason' && route.query.Status === 'Close') {
				router.push({
					name: before_close.value
				})
			} else {
				api.close()
			}
		}
		const onClose = () => close()
		const onCloseCongratulationsModal = () => {
			close('Purchase', {
				product_id: activeProduct.value.id,
				price: Math.abs(activeProduct.value.product_price),
				currency: activeProduct.value.currency
			})
		}

		/** Purchase */
		const purchase = async () => {
			const { id: productId } = activeProduct.value

			api.purchase({
				productId,
				onFinish: finishPurchase,
				onError: (product, text) => {
					console.log(product, text)
				},
				onCancel: () => {},
				closeOnSuccess: false
			})
		}

		/** Scroll */
		const onScrollDone = async () => {
			if (await v$.value.email.$validate()) {
				/** START: Web payments */
				if (billing.canMakeWebPayments.value(activeProduct.value?.currency)) {
					return processingWebPaymentRequest()
				}
				/** END: Web payments */
				purchase()
			}
		}

		const finishPurchase = async () => {
			const { id: productId, type: workbookType } = activeProduct.value

			const options = {
				external_id: userId.value,
				has_workout_workbook: [WORKOUT_WORKBOOK, BUNDLE_WORKBOOK].includes(workbookType),
				has_nutrition_workbook: [NUTRITION_WORKBOOK, BUNDLE_WORKBOOK].includes(workbookType)
			}

			if (!hasEmail.value || !userId.value) {
				store.commit('user/setUserId', md5(data.email))
				options.external_id = userId.value
				options.email = data.email
				options.language = api.getLanguage()
			}

			await appboy.userTrack([options]).then((response) => response.json())

			await appboy.canvas(env('BRAZE_WORKBOOKS_CANVAS_ID'), userId.value)

			amplitude.logEvent('Paywall - Done', {
				Layout: 'Workbooks',
				From: store.getters['getSource'],
				Step: 'Workbooks',
				ProductId: productId,
				Status: 'Purchase'
			})

			showCongratulationsModal.value = true
		}

		/** START: Web payments */
		const isShowingWebPaymentPopup = ref(false)
		const stripeProductId = store.getters['env/getProperty']('stripeProductId')

		const purchaseWeb = (data) => {
			return billing
				.purchase(data.provider, {
					...activeProduct.value,
					...data
				})
				.then(() => {
					isShowingWebPaymentPopup.value = false
					finishPurchase()
				})
		}

		const processingWebPaymentRequest = () => {
			if (lastPaymentMethod.value) {
				purchaseWeb({
					provider: lastPaymentMethod.value,
					stripe_product_id: stripeProductId
				})
			} else {
				isShowingWebPaymentPopup.value = true
			}
		}

		const onPurchase = (data) => {
			if (data.provider == 'InApp') {
				purchase()
			} else {
				purchaseWeb(data)
			}
		}

		/** END: Web payments */

		return {
			t,
			products,
			activeProduct,
			handleSelect,
			purchase,
			v$,
			data,
			showCongratulationsModal,
			onScrollDone,
			isLoading,
			hasEmail,
			onClose,
			onCloseCongratulationsModal,
			closeTheme,
			closePosition,
			language,
			productsTheme,
			isShowingWebPaymentPopup,
			onPurchase,
			onCloseSuccessPopup: () => finishPurchase(),
			workbookDescriptionWorkout,
			workbookDescriptionHealthyEating,
			customClassWorkbookWorkoutDescription
		}
	}
}
</script>

<style lang="scss" scoped>
.top {
	height: 30vh;
	background-color: var(--primary-color, #7d8bf7);
	position: relative;

	img {
		position: absolute;
		top: 4.3vh;
		left: 50%;
		transform: translateX(-55%);
		height: 33.5vh;
	}
}

.subtitle {
	margin-top: 5.5rem;
	text-align: center;
	font-size: 1.2rem;

	@media (min-width: 768px) {
		margin-top: 6.5rem;
	}

	@media (min-width: 991px) {
		margin-top: 8.5rem;
	}
}

.title {
	font-size: 1.6rem;
	line-height: 1.3;
	font-weight: 900;
	text-align: center;
	text-transform: var(--workbooks-title-transform, uppercase);
	font-family: var(--font-family-title);
	margin-bottom: 2rem;
	margin-top: 5.2rem;

	&.primary {
		text-transform: none;
	}

	@media (min-width: 768px) {
		margin-top: 6.5rem;
	}

	@media (min-width: 991px) {
		margin-top: 7.5rem;
	}
}

.form-group {
	margin-bottom: 15px;

	&--label {
		margin-bottom: 0.5rem;
		display: block;
		font-weight: 500;
		text-transform: var(--workbooks-label-text-transform, uppercase);
		text-align: var(--workbooks-form-label-text-align, center);
	}

	&--note {
		font-size: 0.8rem;
		margin-top: 0.5rem;
		padding-left: 0.5rem;
	}

	&.primary {
		.form-control {
			--workbooks-form-control-text-align: center;
		}
	}
}

.form-control {
	display: block;
	width: 100%;
	padding: 1rem 1.3rem;
	font-size: 1.1275rem;
	font-weight: 400;
	line-height: 1.3;
	color: #212529;
	background-color: #fff;
	background-clip: padding-box;
	border: 1px solid #fff;
	-webkit-appearance: none;
	-moz-appearance: none;
	appearance: none;
	border-radius: 0.6rem;
	transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
	text-align: var(--workbooks-form-control-text-align, left);

	&:focus {
		color: #212529;
		background-color: #fff;
		border-color: #86b7fe;
		outline: 0;
	}

	&:disabled {
		border-color: #bfbfbf;
		color: #4a4a4a;
		background-color: transparent;
		opacity: 1;
	}

	&.error {
		border-color: red;
	}
}

.workbooks {
	margin-top: 3rem;
}

.workbook {
	margin-bottom: 2rem;

	&:last-child {
		margin-bottom: 0;
	}

	&-row {
		display: flex;
		align-items: flex-end;
	}

	&--image {
		width: 40%;
	}

	&--title {
		font-size: var(--workbooks-title-font-size, 1.9rem);
		font-weight: var(--workbooks-title-font-weight, 500);
		font-family: var(--font-family-title);
		line-height: 1.2;
		width: 60%;
		margin-bottom: 1rem;

		&.left {
			padding-left: 1.5rem;
		}

		&.right {
			padding-right: 1.5rem;
		}
	}

	&--description {
		font-size: 1.15rem;
		margin-top: 1rem;
	}
}

.left {
	text-align: left;
}

.right {
	text-align: right;
}

[lang='ru'],
[lang='it'] {
	.workbook {
		&--title {
			font-size: 1.6rem;
		}
	}

	.form-control {
		font-size: 1rem;
	}
}
</style>
